/**
 * Substrings of frontend errors that are NOT actionable in our code and should never become a
 * `PORTAL_FRONTEND_ERROR` ticket. They come from the user's environment (browser extensions, page
 * translators, ad-blockers), third-party scripts we don't control (Google reCAPTCHA / Analytics /
 * Adyen), or transient network/stream conditions. Matched case-insensitively against `${name} ${message}`.
 *
 * Kept deliberately explicit (not a broad `NotFoundError` catch-all) so a genuine in-app bug that
 * happens to share an error *type* still gets reported. Tune this list as new noise patterns appear.
 */
const NON_ACTIONABLE_ERROR_PATTERNS = [
	// Version skew / stale chunk after a deploy — handled by a one-shot reload, never a ticket.
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'importing a module script failed',
	'chunkloaderror',
	// Third-party / cross-origin scripts (reCAPTCHA, GA, Adyen…) — not our code.
	'cross-origin script load denied',
	'script error.',
	'invalid site key or not loaded in api.js',
	'recaptcha not loaded',
	'recaptcha has already been rendered',
	// Google reCAPTCHA's own script rejects with `reCAPTCHA Timeout (b)` when its challenge never
	// answers (network blip, blocked iframe); the stack lives entirely in gstatic.com/recaptcha (TEC-3453).
	'recaptcha timeout',
	// Microsoft Outlook/Office SafeLinks scans links in an embedded WebView whose injected script fails
	// with `Object Not Found Matching Id:<N>, MethodName:update, ParamCount:<N>` (TEC-3454, TEC-3327).
	'object not found matching id',
	// An injected script (bot instrumentation / extension) monkey-patches `appendChild` and tries to
	// `JSON.stringify` the node React just mounted. That always throws: React hangs a `__reactFiber$…`
	// property on every host node whose fiber points back at the element, so the cycle is guaranteed
	// (TEC-3452). Matched on the fiber key rather than on `converting circular structure to json`, so a
	// genuine circular-JSON bug in our own serialization still gets reported — we never stringify DOM.
	'__reactfiber',
	// Crypto-wallet extensions (MetaMask, Brave, Coinbase…) inject a provider script that pokes at
	// `window.ethereum` on a page that has no wallet integration at all, so it throws from the
	// injected script's global code (`global code@<document>:1:19`, TEC-3653). We never touch
	// `window.ethereum`, so any error naming it is by definition not ours.
	'window.ethereum',
	// Firefox-iOS and Brave-iOS inject a `__firefox__` reader/scroll helper into every page; it fails
	// on pages it can't parse, as either `Can't find variable: __firefox__` or
	// `undefined is not an object (evaluating 'window.__firefox__.reader')` (TEC-3654).
	'__firefox__',
	// Browser extensions / page translators (Google Translate) mutate the DOM out from under React,
	// making its next insertBefore/removeChild fail. Non-reproducible without the extension.
	"failed to execute 'insertbefore' on 'node'",
	"failed to execute 'removechild' on 'node'",
	'the node before which the new node is to be inserted is not a child of this node',
	'the object can not be found here',
	"cannot read properties of null (reading 'parentnode')",
	// Transient request/stream aborts (user navigated away, connection dropped mid-stream).
	'aborterror',
	'the operation was aborted',
	// View Transitions API rejects `.ready` with `InvalidStateError: Transition was aborted because of
	// invalid state` when a transition is superseded/aborted (re-navigation, tab hidden). Benign (TEC-3546).
	'transition was aborted',
	'the connection was closed',
	'stream lifetime exceeded',
	// Benign layout-observer feedback loop the browser reports as an error.
	'resizeobserver loop',
];

/**
 * A `RangeError: Maximum call stack size exceeded` is only ever produced by runaway
 * recursion, and its stack is the tell-tale: a tight cycle of a handful of frames
 * repeated until the engine's limit. We use that shape (many frames, very few unique)
 * to distinguish a genuine stack overflow from an unrelated error whose message merely
 * contains the phrase — so the filter below stays narrow.
 *
 * Parses both WebKit (`name@url:line:col`) and V8 (`    at name (url:line:col)`) frames.
 */
export function isStackOverflowRecursionCycle(stack: string | undefined): boolean {
	if (!stack) return false;
	const named: string[] = [];
	for (const raw of stack.split('\n')) {
		const line = raw.trim();
		if (!line) continue;
		// V8: "at fnName (...)" | "at fnName" ; WebKit: "fnName@..." ; anonymous frames have no name.
		let name: string | undefined;
		if (line.startsWith('at ')) {
			name = line.slice(3).split(' ')[0];
			if (!name || name.startsWith('(')) name = undefined; // anonymous / "at (native)"
		} else {
			const at = line.indexOf('@');
			if (at > 0) name = line.slice(0, at);
		}
		if (name) named.push(name);
	}
	// A tight recursion cycle: lots of frames, but only a couple of distinct functions.
	return named.length >= 20 && new Set(named).size <= 3;
}

/**
 * Every line of JavaScript this app ships is served from an `/assets/` path — `cdn.worldcoo.com/assets/…`
 * in the deployed stages, same-origin `/assets/…` when the CDN is bypassed. A frame pointing there is
 * therefore the only thing that ties a captured error back to code we can actually change.
 */
const APP_CODE_FRAME_MARKER = '/assets/';

/** V8: `    at fnName (url:line:col)`. */
const V8_FRAME = /^at\s/;
/**
 * WebKit: `fnName@url:line:col`, `fnName@[native code]`, or `fnName@` / `@` when the source has no
 * URL. The part after `@` is matched strictly — a full `scheme://…`, `[native code]`, or nothing —
 * precisely so a *message* that happens to contain an `@` (`Invalid email foo@bar.com`) is not
 * mistaken for a frame. The name may contain spaces because WebKit really does emit some
 * (`global code@…`, `eval code@…`).
 */
const WEBKIT_FRAME = /^.*@(\[native code\]|[a-z][a-z0-9+.-]*:\/\/\S*|)$/i;

/** The lines of a stack that are real frames, skipping the message header V8 puts on the first line. */
function stackFrames(stack: string): string[] {
	return stack
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line !== '' && (V8_FRAME.test(line) || WEBKIT_FRAME.test(line)));
}

/**
 * Whether a stack proves the error came from code we don't ship: it *has* frames, and not one of them
 * is in our own bundle.
 *
 * Injected scripts — extension content scripts, in-app-browser instrumentation, bot harnesses — throw
 * with fully minified identifiers whose names change every time their author rebuilds, so there is no
 * stable message to add to {@link NON_ACTIONABLE_ERROR_PATTERNS}. What they do have in common is that
 * no frame can ever be mapped back to us: on iOS Chrome we saw `Error — Aa` (TEC-3648),
 * `TypeError — undefined is not an object (evaluating 'a.J')` (TEC-3649) and `Error — fa` (TEC-3650),
 * whose stacks carry no URL at all, or an `anonymous@` (`eval`/`new Function`) frame plus a frame at
 * `<document>:415:45` — a line the served document, 51 lines long, does not have.
 *
 * This is not stack censoring of our own code: our chunks are ES modules, always fetched in CORS mode,
 * and the CDN answers `Access-Control-Allow-Origin: *` — which is why a genuine in-app error keeps its
 * full `cdn.worldcoo.com/assets/…` frames (see the React #418 reports, TEC-3651) and is still ticketed.
 *
 * A stack with **no** frames is not evidence of anything, so those keep being reported: the rule only
 * fires when frames exist and all of them are foreign. The accepted cost is that an error thrown from
 * one of the inline SSR shim scripts React and TanStack Start emit into the document would be dropped
 * too; those are vendor streaming glue, and a real fault there surfaces through bundle frames as well.
 */
export function isUnattributableThirdPartyError(stack: string | undefined): boolean {
	if (!stack) return false;
	const frames = stackFrames(stack);
	if (frames.length === 0) return false;
	return !frames.some((frame) => frame.includes(APP_CODE_FRAME_MARKER));
}

/**
 * Adyen's Apple Pay service reacts to a failed merchant validation with, in this order,
 * `console.error(e)`, `this.session.abort()` and `this.options.onError(e)` (a single expression
 * statement in `ApplePay/services/ApplePayService.js`, `onvalidatemerchant`). WebKit's
 * `ApplePaySession.abort()` throws `InvalidAccessError` when the sheet is no longer in an abortable
 * state, and because that throw happens inside a `.catch()` whose promise the `onvalidatemerchant`
 * handler discards, it escapes as an unhandled rejection (TEC-3637).
 *
 * The consequence is what makes it worth detecting: `abort()` throwing is exactly what stops
 * `this.options.onError(e)` from running, so Adyen never tells us the merchant validation failed and
 * the donor is left staring at a closed sheet with no feedback at all. The checkout route listens for
 * this signature to show the donor a message and to report the failure under a name that identifies
 * the Apple Pay path, and the global handler in `router.tsx` skips it so one failure produces one
 * report rather than two.
 *
 * This is deliberately NOT part of {@link shouldReportFrontendError}: we want it reported and
 * ticketed, just with the payment context attached instead of as a bare DOM exception.
 *
 * Gated on the `abort@[native code]` frame: "The object does not support the operation or argument."
 * is WebKit's generic message for *every* `InvalidAccessError`, so without the stack check this would
 * also match an unrelated in-app one.
 */
export function isApplePaySessionAbortError(name: string, message: string, stack: string | undefined): boolean {
	if (!stack) return false;
	return (
		name.toLowerCase().includes('invalidaccesserror') &&
		message.toLowerCase().includes('does not support the operation or argument') &&
		stack.toLowerCase().includes('abort@[native code]')
	);
}

/**
 * Whether the checkout route's own `unhandledrejection` listener is currently mounted, and is therefore
 * the one reporting {@link isApplePaySessionAbortError} with the payment context attached.
 *
 * The global handler defers to it only while it is mounted. Without the flag the global skip is
 * unconditional, so the same signature raised anywhere else — a second Adyen surface, or the sheet's
 * rejection landing after the route unmounts — would be dropped by both handlers and reported by
 * neither. Deferring is about avoiding a *duplicate* report, never about losing the only one.
 *
 * Module state is correct here: there is one browser session per module instance, and the value is
 * read only from an event handler, never during render.
 */
let applePayReporterMounted = false;

export function setApplePayReporterMounted(mounted: boolean): void {
	applePayReporterMounted = mounted;
}

export function isApplePayReporterMounted(): boolean {
	return applePayReporterMounted;
}

/**
 * A rejection whose reason is a raw DOM Event rather than an Error, reported stackless: no name, no
 * message, no frame, nothing triage can act on (TEC-3664). Nothing we ship rejects with one; the
 * candidates are the third-party checkout scripts (Google Pay, Cash App, reCAPTCHA).
 *
 * Matched by PARSED shape rather than exact serialization, because `JSON.stringify(event)` is
 * engine-dependent: V8 has `isTrusted` as an own property (`{"isTrusted":true}`), WebKit and Gecko keep
 * it on the prototype (`{}`), and an Event subclass can carry a stray own property on top. Matching only
 * the exact V8 string left the iOS-Safari half of TEC-3664 arriving forever, and would leave each
 * near-miss shape trickling back as its own ticket. The `stackless` + `ObjectError` gates stay:
 * anything with a frame or a real error name keeps reporting.
 *
 * Complements {@link isUnattributableThirdPartyError}, which needs frames to exist before it can prove
 * they are foreign.
 */
export function isStacklessEventRejection(name: string, message: string, stack: string | undefined): boolean {
	if (stack || name !== 'ObjectError') return false;
	// The message is produced by `safeStringify` (plain JSON.stringify) on a non-Error object.
	let parsed: unknown;
	try {
		parsed = JSON.parse(message);
	} catch {
		return false;
	}
	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
	const keys = Object.keys(parsed);
	// Event-shaped: empty (prototype-held `isTrusted`), or `isTrusted` plus at most a couple of stray own
	// properties. A rejection carrying real content — anything with several fields — stays reportable.
	if (keys.length === 0) return true;
	return 'isTrusted' in parsed && typeof (parsed as { isTrusted: unknown }).isTrusted === 'boolean' && keys.length <= 3;
}

/**
 * A tRPC response that names the CALLER as the fault: FORBIDDEN / 403. Never a system error, whoever
 * caught it — the server refused on purpose (e.g. `claimPaymentToken` refusing a spent payment token
 * after a donor's second tap), told the caller why, and recorded its own wide event, which is where that
 * signal lives. This is the client-side mirror of `CLIENT_FAULT_RPC_CODES` in the wide-event reporter:
 * in the event, out of the ticket board — on both ends.
 *
 * By SHAPE, not by message text: matching the backend's refusal strings would make wording load-bearing
 * across layers, and it silently misses any reporting path that never runs the string filter (the
 * checkout's own catch blocks report directly). Duck-typed rather than `instanceof TRPCClientError` so a
 * stale bundle's foreign class instance still matches.
 */
export function isClientFaultRpcError(error: unknown): boolean {
	if (typeof error !== 'object' || error === null) return false;
	const data = (error as { data?: { code?: unknown; httpStatus?: unknown } }).data;
	if (!data || typeof data !== 'object') return false;
	return data.code === 'FORBIDDEN' || data.httpStatus === 403;
}

/**
 * React error numbers meaning "the server markup and the client's first render disagreed". 418 is the
 * mismatch itself (`text` or `HTML`); the others are the neighbouring hydration failures React can
 * report while recovering the same tree.
 */
const HYDRATION_ERROR_NUMBERS = new Set([418, 421, 422, 423, 425]);

export function isHydrationError(message: string): boolean {
	const number = /minified react error #(\d+)/i.exec(message)?.[1];
	if (number) return HYDRATION_ERROR_NUMBERS.has(Number(number));
	return message.toLowerCase().includes('hydrat');
}

/**
 * The `<html lang>` the DOCUMENT ARRIVED with, captured when this module first evaluates — before
 * hydration, and before a page translator has had a reason to touch a page the visitor is still
 * waiting on. It is the reference {@link isDocumentTranslated} compares against.
 */
const initialDocumentLang = typeof document !== 'undefined' ? document.documentElement.lang : undefined;

/**
 * Whether a page translator has mutated this document. Two signals:
 *
 * - Chrome/Edge stamp `translated-ltr` / `translated-rtl` on `<html>` when Google Translate runs.
 * - Any translator (Safari's, extensions) rewrites `<html lang>` to the TARGET language, so a `lang`
 *   that differs from the one the document arrived with means something rewrote the page after load.
 *
 * Why it exists: translators rewrite text nodes (including number punctuation — a French target turns
 * `7,303.00 €` into `7 303,00 €`) and wrap them in `<font>` elements, which React's hydration reads as
 * text (`#418 args[]=text`) and structural (`args[]=HTML`) mismatches it then recovers from. That
 * produced the entire TEC-3689 → TEC-3696 → TEC-3701 ticket lineage: every victim was a non-English
 * browser (es/it/nl/fr/ru) auto-translating the ENGLISH campaign page — proven by the counter
 * attribution, where one element held `data-counter-locale="en"` (React's, untouched) against
 * es/fr-formatted TEXT (the translator's) under `<html lang>` flipped to the visitor's language.
 *
 * Deliberately NOT part of {@link shouldReportFrontendError}: that filter also runs server-side on
 * POSTed reports, where no DOM exists. Callers gate hydration-class errors on this at capture time.
 *
 * Known trade-off: the in-app language switcher re-renders `<html lang>` too, so after a manual
 * switch this returns true and a hydration-class error would be dropped. Hydration only happens at
 * initial load, before any switch is possible, so nothing real is lost — and a translated document
 * makes any later hydration-shaped report untrustworthy anyway.
 *
 * Parameters exist for tests only; callers use the zero-argument form.
 */
export function isDocumentTranslated(
	html: { className: string; lang: string } | undefined = typeof document !== 'undefined'
		? document.documentElement
		: undefined,
	arrivedLang: string | undefined = initialDocumentLang,
): boolean {
	if (!html) return false;
	if (/(?:^|\s)translated-(?:ltr|rtl)(?:\s|$)/.test(html.className)) return true;
	return !!arrivedLang && !!html.lang && html.lang !== arrivedLang;
}

/**
 * Whether a captured frontend error is worth reporting to the backend (and thus ticketing).
 * Returns false for the environmental / third-party / transient noise listed above.
 */
export function shouldReportFrontendError(name: string, message: string, stack?: string): boolean {
	// Runaway recursion overflowing the (much smaller) WebKit/iOS call stack, thrown deep inside
	// seroval's recursive serialization (a TanStack Start vendor dep) and surfaced to the global
	// error handler (TEC-3592). No application frame is involved and our own payloads are shallow
	// (verified: every campaign-route query is <=depth 9), so there is nothing to fix in our code —
	// it's the same injected-bot mechanism as TEC-3452, just via seroval instead of JSON.stringify.
	// Gated on the recursion-cycle stack shape so we only drop genuine overflows, not an unrelated
	// error that merely mentions the phrase. Caveat: a minified stack can't distinguish a vendor
	// overflow from a hypothetical in-app recursion; accepted because this is the only call-stack
	// System error on record and a real in-app recursion would be deterministic and caught pre-prod.
	if (
		name.toLowerCase().includes('rangeerror') &&
		message.toLowerCase().includes('maximum call stack size exceeded') &&
		isStackOverflowRecursionCycle(stack)
	) {
		return false;
	}

	// Minified junk from an injected script: no stable message to match, but no frame we could ever map
	// back to our own bundle either (TEC-3648, TEC-3649, TEC-3650).
	if (isUnattributableThirdPartyError(stack)) return false;

	// A rejected DOM Event with no stack — a report whose entire content is `{"isTrusted":true}` and which
	// therefore cannot be acted on, from one of the third-party checkout scripts (TEC-3664).
	if (isStacklessEventRejection(name, message, stack)) return false;

	const haystack = `${name} ${message}`.toLowerCase();
	return !NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
}
